import { useEffect, useState, useMemo } from "react";
import {
  Grid, Box, Typography, Chip, Divider, Stack,
  CircularProgress, Backdrop,
  Accordion, AccordionSummary, AccordionDetails,
  Tooltip, IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BiotechIcon from "@mui/icons-material/Biotech";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import { Check, Info, ChartNoAxesCombined, Utensils, Dna, Download, TrendingUp, TrendingDown, Minus, Plus, ArrowDown, Flame, FlaskConical, Link2, Shield, Sprout, Smile, Activity } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ReferenceLine, ReferenceArea, ReferenceDot, Label, LabelList,
} from "recharts";
import { useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "../../../../language";
import { API_URL } from "../../../../constants";
import axios from "axios";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import CustomDialog from "../../../../components/Dialog/DiaCompMedium";
import TextInfoComp from "./TextInfoComp";
import GutBacteriaCategory from "./GutBacteriaCategory";
import GutBacteriaAll from "./GutBacteriaAll";
import GutRecommendations from "./GutRecommendations";
import EnterotypeCard from "./EnterotypeCard";
import InflammatoryPathwaysCard from "./InflammatoryPathwaysCard";
import GutBacteriaComposition from "./GutBacteriaComposition";
import { pressable, pressSx } from "../../../../components/Pressable/pressable";
import { DateSelector } from "../../Cockpit/HealthCockpit";

// ─── Constants ────────────────────────────────────────────────────────────────

const SUBSYSTEM_TO_CATEGORY = {
  1: "microbialHealth",
  2: "gutBodyInteraction",
  3: "gutHealth",
  4: "wellBeing",
  5: "metabolicHormone",
};

const SUBSYSTEM_ICONS = {
  2: Shield,
  3: Sprout,
  4: Smile,
  5: Activity,
};

// Matches ENTEROTYPES in EnterotypeCard.jsx
const ENTEROTYPE_GENUS = {
  1: "Bacteroides",
  2: "Prevotella",
  3: "Ruminococcus",
};

const SUBSYSTEM_TO_INSIGHT_FIELD = {
  1: "key_insights",
  2: "gut_body_interaction",
  3: "gut_health",
  4: "wellbeing",
  5: "metabolic_health",
};

const EXCLUDED_MARKERS = new Set([
  "ECASYN-PWY", "KDO-NAGLIPASYN-PWY", "PWY-6478", "NAGLIPASYN-PWY",
]);


// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFilenameFromUrl(url) {
  try {
    return new URL(url).pathname.split("/").pop() || "report.pdf";
  } catch {
    return "report.pdf";
  }
}

function handleDownload(url) {
  const filename = getFilenameFromUrl(url);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.target = "_blank"; a.rel = "noopener noreferrer";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

// ─── GaugeBar (BloodtestsPanel design) ───────────────────────────────────────

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;

  const val = Number(value);
  const lo = low != null ? Number(low) : null;
  const hi = high != null ? Number(high) : null;

  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5; dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5); dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5); dMax = Math.max(hi * 1.5, val * 1.2);
  }

  const pad = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);

  const span = dMax - dMin;
  const clamp = (v) => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct  = lo != null ? clamp(lo)  : 0;
  const highPct = hi != null ? clamp(hi)  : 100;
  const valPct  = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);

  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{
          position: "absolute", left: `${lowPct}%`, width: `${highPct - lowPct}%`,
          height: "100%", bgcolor: theme.palette.traffic.green, opacity: 0.4,
        }} />
        <Box sx={{
          position: "absolute", top: "50%", left: `${valPct}%`,
          transform: "translate(-50%, -50%)", width: 4, height: 14,
          bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red,
        }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${lowPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {lo}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{
          position: "absolute", top: "100%", left: `${highPct}%`,
          transform: "translateX(-50%)", fontSize: "0.6rem",
          fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
        }}>
          {hi}
        </Typography>
      )}
    </Box>
  );
}

// ─── Detail Section — title + card with content or pending placeholder (OmegaDetails pattern) ──

function DetailSection({ title, children, hasContent, pendingText, accentColor }) {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{
        p: 2.5, borderRadius: 4,
        border: hasContent ? "1px solid" : "1px dashed",
        ...(accentColor ? { overflow: "hidden", borderLeft: `10px solid ${accentColor}` } : {}),
        ...(theme.palette.mode === "light"
          ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
          : { bgcolor: "greydark.C900", borderColor: "greydark.C400" }
        ),
      }}>
        {hasContent ? children : (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center" }}>
            {pendingText}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Subsystem Insight Card (2x2 grid, "Was das für deinen Darm bedeutet") ───

function SubsystemInsightCard({ icon, title, text, emptyText, theme }) {
  const Icon = icon;
  const bodyColor = theme.palette.mode === "dark" ? theme.palette.grey.C400 : theme.palette.greydark.C500;
  return (
    <Accordion disableGutters elevation={0} defaultExpanded={false} sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
        sx={{
          px: 2.5,
          minHeight: 56,
          "& .MuiAccordionSummary-content": { my: 1, display: "flex", alignItems: "center", gap: 1.5 },
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: 2, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          <Icon size={18} color={theme.palette.tertiary.C300} />
        </Box>
        <Typography variant="body1" sx={{ lineHeight: 1.1, color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", fontWeight: 500, fontSize: { xl: "1rem" } }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        {text ? (
          <Typography variant="subtitle1" sx={{ color: bodyColor, lineHeight: 1.75, fontWeight: 400, whiteSpace: "pre-line", fontSize: { xl: "0.9rem" } }}>
            {text}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center" }}>
            {emptyText}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Key Insights Card (structured insight list, replaces plain-text DetailSection for Microbial Health) ──

const KEY_INSIGHT_ICONS = {
  down: ArrowDown,
  flame: Flame,
  flask: FlaskConical,
  link: Link2,
  check: Check,
};

function statusTint(theme, color) {
  return theme.palette.mode === "dark" ? `${color}26` : `${color}1F`;
}

function KeyInsightStatusChip({ label, color }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        flexShrink: 0,
        fontWeight: 700, fontSize: "0.7rem", height: 24,
        color, bgcolor: "transparent",
        border: "1px solid", borderColor: color,
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
}

function KeyInsightRow({ insight, theme, t }) {
  const Icon = KEY_INSIGHT_ICONS[insight.icon] ?? Check;
  const isWarn = insight.status === "warn";
  const color = isWarn ? theme.palette.traffic.orange : theme.palette.traffic.green;
  const chipLabel = isWarn ? t("gut_key_insights_chip_focus") : t("gut_key_insights_chip_stable");
  const bodyColor = theme.palette.mode === "dark" ? theme.palette.grey.C400 : theme.palette.greydark.C500;

  return (
    <Accordion disableGutters elevation={0} defaultExpanded={false} sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
        sx={{
          px: 2.5,
          minHeight: 56,
          "& .MuiAccordionSummary-content": { my: 1, display: "flex", alignItems: "center", gap: 1.5 },
        }}
      >
        <Box sx={{
          width: 32, height: 32, borderRadius: 2, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: statusTint(theme, color),
        }}>
          <Icon size={18} color={color} />
        </Box>
        <Typography variant="body1" sx={{
          lineHeight: 1.1, color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", fontWeight: 500, flexGrow: 1, minWidth: 0,
          fontSize: { xl: "1rem" },
        }}>
          {insight.title}
        </Typography>
        <KeyInsightStatusChip label={chipLabel} color={color} />
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        <Typography variant="subtitle1" sx={{ color: bodyColor, lineHeight: 1.75, fontWeight: 400, fontSize: { xl: "0.9rem" } }}>
          {insight.text}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

function KeyInsightsCard({ title, insights, emptyText, theme, t }) {
  const list = Array.isArray(insights) ? insights : [];
  const focusCount = list.filter(i => i.status === "warn").length;
  const stableCount = list.filter(i => i.status === "good").length;

  return (
    <Box sx={{
      mb: 2, borderRadius: 4, overflow: "hidden",
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ px: 2.5, pt: 2.5, pb: list.length > 0 ? 1.5 : 2.5 }}>
        <Typography variant="body1" fontWeight={700} sx={{
          color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", whiteSpace: "nowrap",
          fontSize: { xl: "1rem" },
        }}>
          {title}
        </Typography>
        {list.length > 0 && (
          <Stack direction="row" spacing={1} flexShrink={0}>
            {focusCount > 0 && (
              <KeyInsightStatusChip
                label={`${focusCount} ${t("gut_key_insights_header_focus")}`}
                color={theme.palette.traffic.orange}
              />
            )}
            {stableCount > 0 && (
              <KeyInsightStatusChip
                label={`${stableCount} ${t("gut_key_insights_header_stable")}`}
                color={theme.palette.traffic.green}
              />
            )}
          </Stack>
        )}
      </Stack>

      {list.length > 0 ? (
        <Stack divider={<Divider sx={{ mx: 2.5, opacity: 0.5 }} />}>
          {list.map((insight, idx) => (
            <KeyInsightRow key={idx} insight={insight} theme={theme} t={t} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", px: 2.5, pb: 2.5 }}>
          {emptyText}
        </Typography>
      )}
    </Box>
  );
}

// ─── Key Marker Card (Leitwerte hero card, OmegaDetails design) ──────────────

function KeyMarkerCard({ marker, onInfo, onChart, variant = "default" }) {
  const theme = useTheme();
  const { t } = useTranslation();
  if (!marker) return null;

  const evaluation = marker.evaluation;
  const statusColor =
    evaluation === "N" ? theme.palette.traffic.green :
    evaluation === "A" ? theme.palette.traffic.orange :
    theme.palette.grey.C400;
  const statusLabel =
    evaluation === "N" ? (t("bt_status_normal") || "NORMAL") :
    evaluation === "A" ? "ABNORMAL" :
    null;
  const statusWord =
    evaluation === "N" ? (t("gut_status_optimal") || "Optimal!") :
    evaluation === "A" ? (t("gut_status_improvable") || "Improvable!") :
    null;

  const segmented = variant === "segmented";

  return (
    <Box sx={{
      flex: "1 1 260px", minWidth: 240,
      p: 2.5, borderRadius: 4, overflow: "hidden",
      bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: theme.palette.mode === "dark"
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box sx={{ maxWidth: "70%" }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center",
            px: 1.75, py: 0.5, mb: 1.5,
            borderRadius: "999px",
            bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha("#fff", 0.6),
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.7)"}`,
            boxShadow: theme.palette.mode === "dark" ? "0 4px 16px rgba(0,0,0,0.4)" : "0 2px 12px rgba(0,0,0,0.05)",
          }}>
            <Typography variant="body1" sx={{
              lineHeight: 1.1, color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", fontWeight: 500,
              // body1 jumps to 1.2rem at xl — too large for this compact card, cap it at the lg size.
              fontSize: { xl: "1rem" },
            }}>
              {t(marker.marker.name_key)}
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{
            fontWeight: 400, lineHeight: 1.5, display: "block",
            // subtitle1 jumps to 1.08rem at xl — same reasoning, cap at the lg size.
            fontSize: { xl: "0.9rem" },
          }}>
            {t(marker.marker.description_key)}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5}>
          {!segmented && statusLabel && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{ bgcolor: statusColor, color: "#fff", fontWeight: 700, fontSize: "0.7rem" }}
            />
          )}
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Info" placement="top">
              <IconButton size="small" onClick={onInfo} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                <Info size={18} color={theme.palette.secondary.main} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("gut_marker_chart")} placement="top">
              <IconButton size="small" onClick={onChart} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
              </IconButton>
            </Tooltip>
          </Stack>
          {segmented && statusWord && (
            <Typography variant="caption" fontWeight={700} sx={{ color: statusColor, whiteSpace: "nowrap" }}>
              {statusWord}
            </Typography>
          )}
        </Stack>
      </Stack>

      {segmented ? (
        <DiversitySegmentedGauge
          value={marker.value}
          unit={marker.unit}
          evaluation={evaluation}
          referenceRange={marker.selected_reference_range}
          markerInfo={marker.marker.marker_info}
          theme={theme}
          t={t}
        />
      ) : (
        <Stack direction="row" alignItems="center" spacing={3}>
          <Typography variant="number0" fontWeight={500} sx={{ lineHeight: 1, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
            {marker.value != null ? Number(marker.value).toFixed(2) : "—"}
            {marker.unit && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.75 }}>
                {marker.unit}
              </Typography>
            )}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <GaugeBar
              value={marker.value}
              low={marker.selected_reference_range?.lower_green}
              high={marker.selected_reference_range?.upper_green}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}

// ─── Value marker — pulsing dot so the current position stands out ──────────

function ValueMarkerDot({ cx, cy, dotColor, ringColor }) {
  return (
    <g>
      {/* pulse uses ringColor (not dotColor) so it contrasts against same-hue segments */}
      <circle cx={cx} cy={cy} r={9.5} fill="none" stroke={ringColor} strokeWidth={3}>
        <animate attributeName="r" values="9.5;12;16;20" keyTimes="0;0.15;0.5;1" dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.9;0.8;0.4;0" keyTimes="0;0.15;0.5;1" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r={9.5} fill={dotColor} stroke={ringColor} strokeWidth={3} />
    </g>
  );
}

// static (non-animated) marker dot — used in dense accordion rows where a
// continuous pulse across 20-30+ simultaneously-visible rows would be noisy
function StaticValueMarkerDot({ cx, cy, dotColor, ringColor }) {
  return <circle cx={cx} cy={cy} r={7} fill={dotColor} stroke={ringColor} strokeWidth={2} />;
}

// ─── Segmented Gauge Bar (recharts strip) — shared by Leitwerte hero cards and accordion rows ──

const GAUGE_SEGMENTS = 32;

function SegmentedGaugeBar({
  value, referenceRange, markerInfo, evaluation, theme,
  segments = GAUGE_SEGMENTS, height = 64, showBracket = false, animated = false, bracketLabel,
}) {
  if (value == null) return null;
  const val = Number(value);

  // Two reference-range shapes have shown up for this API this session
  // (lower_green/upper_green/... for range-based markers vs. green_min/
  // green_max/... for classification-model markers) — read both, whichever
  // is present, rather than assume one.
  const pick = (...keys) => {
    for (const k of keys) {
      if (referenceRange?.[k] != null) return Number(referenceRange[k]);
    }
    return null;
  };
  // lower_green has shown up negative in some API payloads, which isn't a meaningful
  // lower bound for these markers — treat it as its absolute value.
  const lowerGreenRaw  = pick("lower_green", "green_min");
  const lowerGreen     = lowerGreenRaw != null ? Math.abs(lowerGreenRaw) : null;
  const upperGreen     = pick("upper_green", "green_max");
  const lowerOrangeMin = pick("lower_orange_min", "orange_low_min");
  const upperOrangeMax = pick("upper_orange_max", "orange_high_max");
  const classificationThreshold = pick("classification_threshold", "classificationThreshold");

  // marker_info: "1" = higher is always better, "0" = lower is always better, otherwise unknown
  const higherIsBetter = markerInfo === "1";
  const lowerIsBetter  = markerInfo === "0";

  // classificationThreshold-based markers are bounded model probabilities —
  // always a fixed 0-1 scale — not a range derived from lower/upper_orange,
  // which belongs to the older range-based markers and can be an unrelated
  // (and even out-of-0-1) scale for these.
  let dMin, dMax;
  if (classificationThreshold != null) {
    dMin = 0;
    dMax = 1;
  } else {
    const known = [val, lowerOrangeMin, lowerGreen, upperGreen, upperOrangeMax].filter(x => x != null);
    const rawMin = Math.min(...known);
    const rawMax = Math.max(...known);
    const span   = (rawMax - rawMin) || 1;
    dMin = rawMin - span * 0.15;
    dMax = rawMax + span * 0.15;
  }

  const toIdx = (x) => Math.max(0, Math.min(segments - 1,
    Math.round(((x - dMin) / (dMax - dMin)) * (segments - 1))
  ));

  const valueIdx = toIdx(val);

  // Background segments compare bucket indices (cheap, and the 1-segment
  // imprecision is invisible for decorative segments far from the dot).
  // The segment the dot actually sits on (idx === valueIdx) always compares
  // the exact, non-quantized value against the exact boundary instead —
  // otherwise a value and threshold that are closer together than one
  // segment's width can round into the same bucket and disagree about which
  // side of the line that bucket is on, coloring the dot's own segment wrong.
  const thresholdIdx  = classificationThreshold != null ? toIdx(classificationThreshold) : null;
  const lowerGreenIdx = lowerGreen != null ? toIdx(lowerGreen) : null;
  const upperGreenIdx = upperGreen != null ? toIdx(upperGreen) : null;

  const isBelow = (idx, boundaryRaw, boundaryIdx) => idx === valueIdx ? val < boundaryRaw : idx < boundaryIdx;
  const isAbove = (idx, boundaryRaw, boundaryIdx) => idx === valueIdx ? val > boundaryRaw : idx > boundaryIdx;

  // When a threshold exists, marker_info (a fixed semantic property of the
  // marker itself — "this marker is always higher/lower-is-better") is the
  // primary source for which side of it is "good". Only when marker_info
  // doesn't resolve to "1"/"0" do we fall back to inferring direction from
  // evaluation + where this value sits relative to the threshold — evaluation
  // is a per-measurement computed field that can (per observed real data)
  // disagree with other computed fields like status, so it's a fallback,
  // not the primary signal.
  let thresholdHigherIsBetter = null;
  if (thresholdIdx != null) {
    if (higherIsBetter || lowerIsBetter) {
      thresholdHigherIsBetter = higherIsBetter;
    } else if (evaluation === "N" || evaluation === "A") {
      const aboveThreshold = val > classificationThreshold;
      thresholdHigherIsBetter = evaluation === "N" ? aboveThreshold : !aboveThreshold;
    }
  }

  // classificationThreshold, when present, overrules the lower_green/upper_green
  // range and becomes the sole green/orange split point; otherwise fall back
  // to the lower_green/upper_green range (direction from marker_info).
  const segmentColor = (idx) => {
    if (thresholdIdx != null && thresholdHigherIsBetter === true) {
      return isBelow(idx, classificationThreshold, thresholdIdx) ? theme.palette.traffic.orange : theme.palette.traffic.green;
    }
    if (thresholdIdx != null && thresholdHigherIsBetter === false) {
      return isAbove(idx, classificationThreshold, thresholdIdx) ? theme.palette.traffic.orange : theme.palette.traffic.green;
    }
    if (higherIsBetter) {
      return (lowerGreenIdx != null && isBelow(idx, lowerGreen, lowerGreenIdx)) ? theme.palette.traffic.orange : theme.palette.traffic.green;
    }
    if (lowerIsBetter) {
      return (upperGreenIdx != null && isAbove(idx, upperGreen, upperGreenIdx)) ? theme.palette.traffic.orange : theme.palette.traffic.green;
    }
    if (lowerGreenIdx != null && isBelow(idx, lowerGreen, lowerGreenIdx)) return theme.palette.traffic.orange;
    if (upperGreenIdx != null && isAbove(idx, upperGreen, upperGreenIdx)) return theme.palette.traffic.orange;
    return theme.palette.traffic.green;
  };

  const data      = Array.from({ length: segments }, (_, i) => ({ idx: i, v: 1 }));
  const lowIdx    = lowerGreenIdx;
  const highIdx   = upperGreenIdx;

  const markerColor = evaluation === "N" ? theme.palette.traffic.green : theme.palette.traffic.orange;
  // With showBracket, extra headroom above the bar is needed for the "Average"
  // bracket/label. Without it (accordion rows), that headroom just pushes the
  // bar and dot down off-center relative to the row's value text — so use a
  // tight domain matching the bar's own height, with symmetric margins, to
  // keep the bar+dot vertically centered in their box.
  const topMargin    = showBracket ? 22 : 6;
  const bottomMargin = showBracket ? 0 : 6;
  const yDomain       = showBracket ? [0, 1.4] : [0, 1];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: topMargin, right: 6, left: 6, bottom: bottomMargin }} barCategoryGap="20%">
        <XAxis dataKey="idx" type="category" hide />
        <YAxis type="number" domain={yDomain} hide />
        <Bar dataKey="v" isAnimationActive={false} radius={[2, 2, 2, 2]}>
          {data.map(d => <Cell key={d.idx} fill={segmentColor(d.idx)} />)}
        </Bar>
        {showBracket && lowIdx != null && highIdx != null && (
          <>
            <ReferenceArea
              x1={lowIdx} x2={highIdx} y1={1.2} y2={1.24}
              fill={theme.palette.text.secondary} fillOpacity={0.45} stroke="none"
            >
              {bracketLabel && (
                <Label
                  value={bracketLabel}
                  position="top"
                  fill={theme.palette.text.secondary}
                  fontSize={11}
                  fontWeight={700}
                />
              )}
            </ReferenceArea>
            <ReferenceArea x1={lowIdx} x2={lowIdx} y1={1.1} y2={1.24} fill={theme.palette.text.secondary} fillOpacity={0.45} stroke="none" />
            <ReferenceArea x1={highIdx} x2={highIdx} y1={1.1} y2={1.24} fill={theme.palette.text.secondary} fillOpacity={0.45} stroke="none" />
          </>
        )}
        <ReferenceDot
          x={valueIdx} y={0.5}
          shape={
            animated
              ? <ValueMarkerDot dotColor={markerColor} ringColor={theme.palette.background.paper} />
              : <StaticValueMarkerDot dotColor={markerColor} ringColor={theme.palette.background.paper} />
          }
          isFront
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Diversity Segmented Gauge (hero value, picture-guided) ─────────────────

function DiversitySegmentedGauge({ value, unit, evaluation, referenceRange, markerInfo, theme, t }) {
  if (value == null) return null;
  const val = Number(value);

  const markerColor = evaluation === "N" ? theme.palette.traffic.green : theme.palette.traffic.orange;

  return (
    <Box>
      <Typography variant="h4" fontWeight={500} sx={{ color: markerColor, lineHeight: 1, fontVariantNumeric: "tabular-nums", mb: 1 }}>
        {val.toFixed(2)}
        {unit && (
          <Typography component="span" variant="body2" sx={{ color: markerColor, ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Typography>

      <Box sx={{ maxWidth: 260 }}>
        <SegmentedGaugeBar
          value={value}
          referenceRange={referenceRange}
          markerInfo={markerInfo}
          evaluation={evaluation}
          theme={theme}
          height={32}
          animated
        />
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.6rem" }}>
            {t("gut_leitwerte_low") || "Low"}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.6rem" }}>
            {t("gut_leitwerte_high") || "High"}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Gut Marker Chart ─────────────────────────────────────────────────────────

function GutMarkerChart({ chartData, markerInfo, theme, t }) {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { chartPoints, unit } = chartData;

  const refLow  = chartPoints.find(p => p.ref_low  != null)?.ref_low;
  const refHigh = chartPoints.find(p => p.ref_high != null)?.ref_high;
  const values  = chartPoints.map(d => d.value);
  const allNums = [...values, refLow, refHigh].filter(x => x != null);
  const span    = Math.max(...allNums) - Math.min(...allNums) || 1;
  const pad     = span * 0.3;
  const yMin    = parseFloat((Math.min(...allNums) - pad).toFixed(4));
  const yMax    = parseFloat((Math.max(...allNums) + pad).toFixed(4));

  // marker_info: "1" = higher is always better, "0" = lower is always better, otherwise two-sided
  const higherIsBetter = markerInfo === "1";
  const lowerIsBetter  = markerInfo === "0";

  const green   = theme.palette.traffic.green;
  const orange  = theme.palette.traffic.orange;
  const primary = theme.palette.primary.main;

  const dotColor = (status) => (status === "green" ? green : orange);

  const CustomDot = ({ cx, cy, payload }) => (
    <circle cx={cx} cy={cy} r={6}
      fill={dotColor(payload.status)}
      stroke={theme.palette.background.paper} strokeWidth={2}
    />
  );

  const ValueLabel = ({ x, y, value }) => (
    <text x={x} y={y - 13} textAnchor="middle" fontSize={11} fontWeight={700}
      fill={theme.palette.text.primary}>
      {Number(value).toFixed(2)}
    </text>
  );

  return (
    <Box sx={{ width: "100%", pt: 0, px: 0 }}>
      {unit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: { xs: 1.5, sm: 2.5 } }}>
          <Chip
            label={`${t("bt_chart_unit")}: ${unit}`}
            size="small"
            sx={{ fontWeight: 700, fontSize: { xs: "0.68rem", sm: "0.8rem" }, letterSpacing: 0.3 }}
          />
        </Box>
      )}
      <ResponsiveContainer width="100%" {...(isMobile ? { aspect: 1.3 } : { height: 340 })}>
        <LineChart data={chartPoints} margin={{ top: 30, right: isMobile ? 12 : 28, left: isMobile ? -4 : 4, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
          {higherIsBetter && refLow != null && (
            <ReferenceArea y1={refLow} y2={yMax} fill={green} fillOpacity={0.07} stroke="none"
              label={{ value: t("bt_chart_ref_range"), position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }}
            />
          )}
          {lowerIsBetter && refHigh != null && (
            <ReferenceArea y1={yMin} y2={refHigh} fill={green} fillOpacity={0.07} stroke="none"
              label={{ value: t("bt_chart_ref_range"), position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }}
            />
          )}
          {!higherIsBetter && !lowerIsBetter && refLow != null && refHigh != null && (
            <ReferenceArea y1={refLow} y2={refHigh} fill={green} fillOpacity={0.07} stroke="none"
              label={{ value: t("bt_chart_ref_range"), position: "insideTopRight", fontSize: 10, fill: green, fontWeight: 600 }}
            />
          )}
          {refLow  != null && !lowerIsBetter  && <ReferenceLine y={refLow}  stroke={green} strokeDasharray="5 3" strokeWidth={1.5} />}
          {refHigh != null && !higherIsBetter && <ReferenceLine y={refHigh} stroke={green} strokeDasharray="5 3" strokeWidth={1.5} />}
          <XAxis dataKey="label"
            tick={{ fontSize: isMobile ? 11 : 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} padding={{ left: isMobile ? 24 : 32, right: isMobile ? 8 : 32 }}
          />
          <YAxis domain={[yMin, yMax]}
            tick={{ fontSize: isMobile ? 11 : 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
            axisLine={{ stroke: theme.palette.text.primary, strokeWidth: 0.75 }}
            tickLine={false} width={isMobile ? 42 : 60}
            tickFormatter={(v) => Number(v).toFixed(2)}
          />
          <RechartsTooltip
            contentStyle={{
              borderRadius: 10, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)", fontSize: 13,
            }}
            formatter={(value) => [`${Number(value).toFixed(2)}${unit ? ` ${unit}` : ""}`, t("bt_chart_value")]}
          />
          <Line type="monotone" dataKey="value" stroke={primary} strokeWidth={2.5}
            dot={<CustomDot />} activeDot={{ r: 9, stroke: primary, strokeWidth: 2 }}>
            <LabelList dataKey="value" content={<ValueLabel />} />
          </Line>
        </LineChart>
      </ResponsiveContainer>

      {/* History table */}
      <Box sx={{ mt: { xs: 1.5, sm: 3 }, borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
        <Box sx={{
          display: "grid", gridTemplateColumns: { xs: "2fr 1fr 1fr 1fr", sm: "2fr 1fr 1fr 1fr 1fr" },
          px: { xs: 1, sm: 2 }, py: 1,
          bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
        }}>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {t("bt_chart_col_date")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {t("bt_chart_col_value")}
          </Typography>
          <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {t("bt_chart_col_unit")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {t("bt_chart_col_trend")}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary" }}>
            {t("bt_chart_col_status")}
          </Typography>
        </Box>
        {[...chartPoints].reverse().map((row, idx, arr) => {
          const prev      = arr[idx + 1];
          const delta     = prev != null ? row.value - prev.value : null;
          const TrendIcon = delta == null
            ? null
            : Math.abs(delta) < 0.001
              ? Minus
              : delta > 0 ? TrendingUp : TrendingDown;
          const trendColor = delta == null
            ? theme.palette.text.disabled
            : Math.abs(delta) < 0.001
              ? theme.palette.text.secondary
              : row.status === "green" ? green : orange;
          const isEven = idx % 2 === 0;
          return (
            <Box key={row.date} sx={{
              display: "grid", gridTemplateColumns: { xs: "2fr 1fr 1fr 1fr", sm: "2fr 1fr 1fr 1fr 1fr" },
              px: { xs: 1, sm: 2 }, py: 0.9, alignItems: "center",
              bgcolor: isEven
                ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C900"),
            }}>
              <Typography variant="body2" fontWeight={600} color={theme.palette.mode === "light" ? "greydark.C900" : "grey.C50"}>
                {new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {row.value.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                {row.unit ?? "—"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {TrendIcon
                  ? <TrendIcon size={22} color={trendColor} strokeWidth={2.5} />
                  : <Minus size={22} color={theme.palette.text.disabled} strokeWidth={2} />
                }
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {row.status === "green"
                  ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                  : row.status != null
                    ? (
                      <Chip
                        label="ABNORMAL"
                        size="small"
                        sx={{
                          bgcolor: "transparent", color: theme.palette.traffic.orange,
                          border: "1px solid", borderColor: theme.palette.traffic.orange,
                          fontWeight: 700, fontSize: "0.65rem", height: 22, borderRadius: "6px",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )
                    : <Typography variant="body2" color="text.secondary">—</Typography>
                }
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GutMicrobiomeDashboard() {
  const theme = useTheme();
  const { t } = useTranslation();

  // ── State ────────────────────────────────────────────────────────────────
  const [dateItems, setDateItems]           = useState([]);   // full API objects
  const [selectedDate, setSelectedDate]     = useState(null);
  const [measurements, setMeasurements]     = useState([]);
  const [datesLoading, setDatesLoading]     = useState(true);
  const [measurementsLoading, setMeasurementsLoading] = useState(false);
  const [error, setError]                   = useState(null);
  const [recommendations, setRecommendations] = useState({});
  const [recsLoading, setRecsLoading]       = useState(true);
  const [type, setType]                     = useState(null);
  const [lps, setLps]                       = useState(null);
  const [agentMicrobiomeData, setAgentMicrobiomeData] = useState([]); // all fields, all kits — full endpoint response
  const [bacteriaRecords, setBacteriaRecords] = useState([]);
  const [bacteriaLoading, setBacteriaLoading] = useState(false);

  // dialogs
  const [openInfo, setOpenInfo]             = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [openChart, setOpenChart]           = useState(false);
  const [chartLoading, setChartLoading]     = useState(false);
  const [chartData, setChartData]           = useState(null);
  const [chartCache, setChartCache]         = useState({});
  const [openRecs, setOpenRecs]             = useState(false);
  const [recsDialogSubsystem, setRecsDialogSubsystem] = useState(null);
  const [bacteriaCategoryOpen, setBacteriaCategoryOpen] = useState(false);
  const [bacteriaAllOpen, setBacteriaAllOpen] = useState(false);
  const [openSubsystems, setOpenSubsystems] = useState(() => new Set());
  const [enterotypeOpen, setEnterotypeOpen]   = useState(false);

  // ── Fetch available dates (with metadata) ───────────────────────────────
  useEffect(() => {
    axios
      .get(`${API_URL}/api/gut/measurements-available-dates/`)
      .then(res => {
        if (Array.isArray(res.data)) {
          const sorted = [...res.data].sort((a, b) => new Date(b.date) - new Date(a.date));
          setDateItems(sorted);
          setSelectedDate(sorted[0]?.date ?? null);
        }
      })
      .catch(() => setError("Failed to fetch available test dates."))
      .finally(() => setDatesLoading(false));
  }, []);

  // ── Fetch measurements for selected date ─────────────────────────────────
  useEffect(() => {
    if (!selectedDate) return;
    const controller = new AbortController();
    setMeasurementsLoading(true);
    axios
      .get(`${API_URL}/api/gut/measurements-detail/?date=${selectedDate}`, { signal: controller.signal })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        setMeasurements(data);
        setType(data.find(m => m.marker?.id === 5) ?? null);
        const lpsData = data.filter(m => m.marker?.name && EXCLUDED_MARKERS.has(m.marker.name));
        setLps(lpsData.length > 0 ? lpsData : null);
      })
      .catch(err => { if (err.name !== "CanceledError") setError("Failed to fetch measurements."); })
      .finally(() => setMeasurementsLoading(false));
    return () => controller.abort();
  }, [selectedDate]);

  // ── Fetch bacteria records for selected date (shared by composition charts) ──
  useEffect(() => {
    if (!selectedDate) return;
    const controller = new AbortController();
    setBacteriaLoading(true);
    axios
      .get(`${API_URL}/api/gut/bacteria-records/?date=${selectedDate}`, { signal: controller.signal })
      .then(res => setBacteriaRecords(Array.isArray(res.data) ? res.data : []))
      .catch(err => { if (err.name !== "CanceledError") console.error("Fetch error:", err); })
      .finally(() => setBacteriaLoading(false));
    return () => controller.abort();
  }, [selectedDate]);

  // ── Fetch recommendations for selected date ──────────────────────────────
  useEffect(() => {
    if (!selectedDate) return;
    setRecsLoading(true);
    axios
      .get(`${API_URL}/api/gut/biome-recommendations/`)
      .then(res => {
        const filtered = res.data.filter(item => item.date === selectedDate);
        const grouped = filtered.reduce((acc, item) => {
          const { category, title, description, pointer_intro, pointers,
            recipe_title, ingredients, preparation, conclusion } = item;
          if (!acc[category]) acc[category] = { items: [], recipe: null };
          acc[category].items.push({ title, description, pointer_intro, pointers });
          if (!acc[category].recipe && (recipe_title || ingredients || preparation || conclusion)) {
            acc[category].recipe = { recipe_title, ingredients, preparation, conclusion };
          }
          return acc;
        }, {});
        setRecommendations(grouped);
      })
      .catch(console.error)
      .finally(() => setRecsLoading(false));
  }, [selectedDate]);

  // ── Fetch AI microbiome insights (all fields, all kits) ──────────────────
  useEffect(() => {
    axios
      .get(`${API_URL}/api/gut/agent-microbiome/`)
      .then(res => setAgentMicrobiomeData(Array.isArray(res.data) ? res.data : []))
      .catch(console.error);
  }, []);

  // ── Derived: ordered subsystem groups ───────────────────────────────────
  const orderedGroups = useMemo(() => {
    if (!Array.isArray(measurements)) return [];
    const grouped = measurements
      .filter(m => !(m.marker?.name && EXCLUDED_MARKERS.has(m.marker.name)))
      .reduce((acc, m) => {
        const sub = m.marker?.healthsubsystem ?? "unknown";
        if (!acc[sub]) acc[sub] = [];
        acc[sub].push(m);
        return acc;
      }, {});
    return Object.entries(grouped)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([sub, items]) => [
        sub,
        [...items].sort((a, b) => (a.marker?.order ?? 999) - (b.marker?.order ?? 999)),
      ]);
  }, [measurements]);

  // ── Derived: Leitwerte (Alpha-Diversity id 1, Dysbiosis Index id 4) from accordion 1 ──
  const leitwerteMarkers = useMemo(() => {
    const accordion1Items = orderedGroups[0]?.[1] ?? [];
    return {
      diversity: accordion1Items.find(m => m.marker?.id === 1) ?? null,
      dysbiosis: accordion1Items.find(m => m.marker?.id === 4) ?? null,
    };
  }, [orderedGroups]);

  // ── Active recommendations for dialog ───────────────────────────────────
  const activeRecsItems = useMemo(() => {
    if (!recsDialogSubsystem) return { items: [], recipe: null };
    const key = SUBSYSTEM_TO_CATEGORY[recsDialogSubsystem];
    return recommendations[key] ?? { items: [], recipe: null };
  }, [recsDialogSubsystem, recommendations]);

  // ── Derived: date strings for carousel + selected date metadata ──────────
  const dates        = useMemo(() => dateItems.map(d => d.date), [dateItems]);
  const selectedItem = useMemo(() => dateItems.find(d => d.date === selectedDate) ?? null, [dateItems, selectedDate]);

  // ── Derived: AI microbiome insights for the selected kit ─────────────────
  const agentMicrobiome = useMemo(
    () => agentMicrobiomeData.find(r => r.kitId === selectedItem?.kitId) ?? null,
    [agentMicrobiomeData, selectedItem]
  );

  // ── PDF download: re-fetch for a fresh signed URL before triggering ──────
  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/gut/measurements-available-dates/`);
      if (Array.isArray(res.data)) {
        const fresh = res.data.find(d => d.date === selectedDate);
        if (fresh?.pdf_download_url) handleDownload(fresh.pdf_download_url);
      }
    } catch (err) {
      console.error("Failed to refresh PDF URL", err);
    }
  };

  // ── Subsystem labels ─────────────────────────────────────────────────────
  const subsystemLabels = {
    1: t("subsystemMicrobialHealth"),
    2: t("subsystemGutBodyInteraction"),
    3: t("subsystemGutHealth"),
    4: t("subsystemWellBeing"),
    5: t("subsystemMetabolicHealth"),
  };

  // ── Chart helpers ────────────────────────────────────────────────────────
  const transformChartData = (data) => {
    if (!data?.points) return null;
    const refLow  = data.reference?.lower;
    const refHigh = data.reference?.upper;
    const chartPoints = data.points.map(p => ({
      date:     p.date,
      label:    new Date(p.date + "T00:00:00").toLocaleDateString("en", { month: "short", day: "numeric", year: "2-digit" }),
      value:    Number(p.value),
      ref_low:  refLow  != null ? Number(refLow)  : undefined,
      ref_high: refHigh != null ? Number(refHigh) : undefined,
      status:   p.status,
      unit:     data.unit,
    }));
    const latest = chartPoints[chartPoints.length - 1];
    return {
      chartPoints,
      unit: data.unit,
      latestColor:
        latest?.status === "green"  ? theme.palette.traffic.green  :
        latest?.status === "orange" ? theme.palette.traffic.orange :
        theme.palette.traffic.red,
    };
  };

  const fetchMarkerData = async (marker) => {
    const id = marker.marker.id;
    if (chartCache[id]) { setChartData(chartCache[id]); return; }
    setChartLoading(true);
    setChartData(null);
    try {
      const res = await axios.get(`${API_URL}/api/gut/marker-timeseries/?marker_id=${id}`);
      const transformed = transformChartData(res.data);
      setChartData(transformed);
      setChartCache(prev => ({ ...prev, [id]: transformed }));
    } catch (err) {
      console.error("Chart fetch failed", err);
    } finally {
      setChartLoading(false);
    }
  };

  const handleOpenChart = (e, marker) => {
    e.stopPropagation();
    setSelectedMarker(marker);
    setOpenChart(true);
    fetchMarkerData(marker);
  };

  // ── Loading / empty states ───────────────────────────────────────────────
  if (datesLoading) {
    return (
      <Backdrop open sx={{ zIndex: t => t.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (!dateItems || dateItems.length === 0) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography color="text.secondary">{t("gut_no_results")}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const headingColor = theme.palette.mode === "dark" ? "grey.C50" : "greydark.C900";
  const metaLabelSx = {
    fontSize: "0.85rem", fontWeight: 600, lineHeight: 1.3,
    color: theme.palette.mode === "dark" ? "grey.C400" : "greydark.C500",
  };
  const detailValueSx = { fontSize: "0.98rem", fontWeight: 600, lineHeight: 1.35, color: headingColor };

  return (
    <Grid container justifyContent="center" sx={{ mt: 1, mb: 10 }}>

      {/* ── Start card: title + date dropdown | kit details ───────────────── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
        <Box sx={{
          width: "100%", maxWidth: 800, p: { xs: 2.25, sm: 3 }, borderRadius: "24px",
          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
          backdropFilter: "blur(28px) saturate(180%)",
          WebkitBackdropFilter: "blur(28px) saturate(180%)",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
          boxShadow: theme.palette.mode === "dark"
            ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
            : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
        }}>

          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.15fr)" },
            columnGap: 4, rowGap: 2.5, alignItems: "start",
          }}>
            {/* Left: title + test date dropdown */}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: -0.05, color: headingColor }}>
                {t("gut_header")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <DateSelector
                  dates={[...dates].sort((a, b) => b.localeCompare(a))}
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                />
              </Box>
            </Box>

            {/* Right: kit details, right-aligned values */}
            <Box component="dl" sx={{ m: 0, display: "flex", flexDirection: "column" }}>
              {[
                ...(selectedItem?.kitId != null ? [{
                  key: "kit",
                  label: t("gut_kit_id"),
                  value: <Typography sx={{ ...detailValueSx, fontVariantNumeric: "tabular-nums" }}>{selectedItem.kitId}</Typography>,
                }] : []),
                {
                  key: "sample",
                  label: t("gut_sample_label"),
                  value: <Typography sx={detailValueSx}>{t("gut_sample_value")}</Typography>,
                },
                {
                  key: "pdf",
                  label: t("bt_raw_report_download"),
                  value: (
                    <Tooltip title={t("bt_raw_report_download")} placement="top">
                      <span>
                        <IconButton
                          disabled={!selectedItem?.pdf_download_url}
                          onClick={handleDownloadPdf}
                          aria-label={t("bt_raw_report_download")}
                          sx={{
                            width: 36, height: 36,
                            bgcolor: theme.palette.greydark.C900, color: "#fff",
                            border: theme.palette.mode === "dark" ? "1px solid rgba(255,255,255,0.14)" : "none",
                            transition: "filter 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                            "& .gut-arrow-down": { transition: "transform 200ms cubic-bezier(0.23, 1, 0.32, 1)" },
                            "@media (hover: hover) and (pointer: fine)": {
                              "&:hover": { bgcolor: theme.palette.greydark.C900, filter: "brightness(1.2)" },
                              "&:hover .gut-arrow-down": { transform: "translateY(2px)" },
                            },
                            "&:active": { transform: "scale(0.94)" },
                            "&.Mui-disabled": { bgcolor: alpha(theme.palette.greydark.C900, 0.25), color: "#fff" },
                          }}
                        >
                          <Box className="gut-arrow-down" sx={{ display: "flex" }}><Download size={17} strokeWidth={2.5} /></Box>
                        </IconButton>
                      </span>
                    </Tooltip>
                  ),
                },
              ].map((row, i) => (
                <Box key={row.key}>
                  {i > 0 && <Divider sx={{ borderColor: alpha(theme.palette.mode === "dark" ? "#fff" : theme.palette.greydark.C900, 0.08) }} />}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, minHeight: 52, py: 0.75, ...(i === 0 && { pt: 0, minHeight: 44 }) }}>
                    <Typography component="dt" sx={metaLabelSx}>{row.label.replace(/:\s*$/, "")}:</Typography>
                    <Box component="dd" sx={{ m: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, minWidth: 0, textAlign: "right" }}>
                      {row.value}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.mode === "dark" ? "#fff" : theme.palette.greydark.C900, 0.08) }} />


          {/* Compact widgets: enterotype + bacteria analysis + bacteria summary */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>

            {/* Gut Microbiome Profile (Enterotype) */}
            {(() => {
              const active = Boolean(type);
              const typeId = active ? Math.round(Number(type.value)) : null;
              return (
                <Box {...pressable(active ? () => setEnterotypeOpen(true) : undefined)} sx={{ ...pressSx,
                  flex: 1, minWidth: 150, display: "flex", alignItems: "center", gap: 1,
                  p: 0.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider",
                  cursor: active ? "pointer" : "default", opacity: active ? 1 : 0.45,
                  transition: "border-color 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                  "&:hover": active ? { borderColor: "primary.main" } : {},
                }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800" }}>
                    <Dna size={16} color={theme.palette.text.secondary} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary", display: "block", fontSize: "0.55rem" }}>
                      {t("gut_widget_profile_label")}
                    </Typography>
                    <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: "0.72rem", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {active
                        ? `${t("gut_widget_profile_subtitle")} · ${typeId}${ENTEROTYPE_GENUS[typeId] ? ` – ${ENTEROTYPE_GENUS[typeId]}` : ""}`
                        : "—"}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            {/* Bacteria by Category */}
            <Box {...pressable(() => setBacteriaCategoryOpen(true))} sx={{ ...pressSx,
              flex: 1, minWidth: 150, display: "flex", alignItems: "center", gap: 1,
              p: 0.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider",
              cursor: "pointer", transition: "border-color 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              "&:hover": { borderColor: "primary.main" },
            }}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800" }}>
                <BiotechIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary", display: "block", fontSize: "0.55rem" }}>
                  {t("gut_widget_bacteria_analysis_label")}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: "0.72rem", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t("gut_widget_bacteria_analysis_subtitle")}
                </Typography>
              </Box>
            </Box>

            {/* All Bacteria */}
            <Box {...pressable(() => setBacteriaAllOpen(true))} sx={{ ...pressSx,
              flex: 1, minWidth: 150, display: "flex", alignItems: "center", gap: 1,
              p: 0.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider",
              cursor: "pointer", transition: "border-color 0.15s, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
              "&:hover": { borderColor: "primary.main" },
            }}>
              <Box sx={{ width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800" }}>
                <BubbleChartIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary", display: "block", fontSize: "0.55rem" }}>
                  {t("gut_widget_bacteria_summary_label")}
                </Typography>
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: "0.72rem", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {t("gut_widget_bacteria_summary_subtitle")}
                </Typography>
              </Box>
            </Box>

          </Box>

        </Box>
      </Grid>

      {/* ── Leitwerte ────────────────────────────────────────────────────── */}
      {!measurementsLoading && (leitwerteMarkers.diversity || leitwerteMarkers.dysbiosis) && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", mb: 1, whiteSpace: "nowrap" }}>
              {t("gut_leitwerte_title") || "Leitwerte"}
            </Typography>
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap" }}>
              <KeyMarkerCard
                marker={leitwerteMarkers.diversity}
                onInfo={e => { e.stopPropagation(); setSelectedMarker(leitwerteMarkers.diversity); setOpenInfo(true); }}
                onChart={e => handleOpenChart(e, leitwerteMarkers.diversity)}
                variant="segmented"
              />
              <KeyMarkerCard
                marker={leitwerteMarkers.dysbiosis}
                onInfo={e => { e.stopPropagation(); setSelectedMarker(leitwerteMarkers.dysbiosis); setOpenInfo(true); }}
                onChart={e => handleOpenChart(e, leitwerteMarkers.dysbiosis)}
                variant="segmented"
              />
            </Stack>
          </Box>
        </Grid>
      )}

      {/* ── Key Insights (Microbial Health) ─────────────────────────────── */}
      {!measurementsLoading && (
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <KeyInsightsCard
              title={t("gut_key_insights_title_short") || "Your Key Insights"}
              insights={agentMicrobiome?.key_insights_array}
              emptyText={t("gut_key_insights_empty") || "No analysis available"}
              theme={theme}
              t={t}
            />
          </Box>
        </Grid>
      )}

      {/* ── Test Results Card (transparent) ─────────────────────────────── */}
      <Grid size={12} sx={{ display: "flex", justifyContent: "center", px: 2, mb: 3 }}>
        <Box sx={{ width: "100%", maxWidth: 800 }}>

          {/* ── Subsystem Accordions ─────────────────────────────────────────────── */}
          <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", mb: 1, whiteSpace: "nowrap" }}>
            {t("gut_biomarker_overview_title") || "Biomarker Overview"}
          </Typography>
          {measurementsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            orderedGroups
              .filter(([sub]) => Number(sub) !== 6)
              .map(([subsystem, items]) => {
                const subsystemId    = Number(subsystem);
                const categoryKey    = SUBSYSTEM_TO_CATEGORY[subsystemId];
                const hasRecs        = !recsLoading && categoryKey && (recommendations[categoryKey]?.items?.length ?? 0) > 0;
                const outOfRangeCount = items.filter(m => m.evaluation === "A").length;
                const label          = subsystemLabels[subsystemId] || `Subsystem ${subsystemId}`;
                const isOpen         = openSubsystems.has(subsystemId);
                const toggleOpen     = () => {
                  setOpenSubsystems((prev) => {
                    const next = new Set(prev);
                    if (next.has(subsystemId)) next.delete(subsystemId); else next.add(subsystemId);
                    return next;
                  });
                };

                return (
                  <Grid size={12} key={subsystem} sx={{ mb: 2 }}>
                    <Box sx={{ width: "100%", maxWidth: 800 }}>
                      <Accordion
                        expanded={isOpen}
                        onChange={toggleOpen}
                        disableGutters
                        elevation={0}
                        slotProps={{ transition: { timeout: 320 } }}
                        sx={{
                          borderRadius: "16px !important",
                          overflow: "hidden",
                          "&:before": { display: "none" },
                          bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                          backdropFilter: "blur(28px) saturate(180%)",
                          WebkitBackdropFilter: "blur(28px) saturate(180%)",
                          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
                          boxShadow: theme.palette.mode === "dark"
                            ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                            : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
                        }}
                      >
                        <AccordionSummary
                          aria-label={label}
                          sx={{
                            px: { xs: 1.5, sm: 2.5 }, pt: 1.25, pb: 1.25,
                            overflow: "hidden",
                            borderTopLeftRadius: "16px !important", borderTopRightRadius: "16px !important",
                            bgcolor: "transparent",
                            minHeight: 44,
                            alignItems: { xs: "flex-start", sm: "center" },
                            "& .MuiAccordionSummary-content": { my: 0.5, width: "100%", minWidth: 0, mr: 0 },
                            "& .MuiAccordionSummary-expandIconWrapper": { display: "none" },
                          }}
                        >
                          <Box sx={{ width: "100%" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                                {label}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              {hasRecs && (
                                <Tooltip title={t("gut_view_recommendations")} placement="top">
                                  <Stack
                                    direction="row" alignItems="center" spacing={0.75}
                                    onClick={e => { e.stopPropagation(); setRecsDialogSubsystem(subsystemId); setOpenRecs(true); }}
                                    sx={{
                                      display: { xs: "none", sm: "flex" },
                                      mr: 1, px: 1.5, height: 28, borderRadius: "999px", flexShrink: 0,
                                      bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
                                      cursor: "pointer", transition: "transform 0.15s",
                                      "&:hover": { transform: "scale(1.05)" },
                                    }}
                                  >
                                    <Utensils size={14} color={theme.palette.text.secondary} />
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                      {t("gut_food_recommendations_label") || "Nutrition Recommendations"}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              )}
                              {outOfRangeCount > 0 ? (
                                <Tooltip title={t("gut_out_of_range_tooltip")} placement="top">
                                  <Chip
                                    label={`${outOfRangeCount}`}
                                    size="small"
                                    sx={{
                                      width: 24, height: 24, borderRadius: "50%",
                                      bgcolor: "transparent", color: theme.palette.traffic.orange,
                                      border: "1px solid", borderColor: theme.palette.traffic.orange,
                                      fontWeight: 700, fontSize: "0.75rem",
                                      "& .MuiChip-label": { p: 0, width: "100%", textAlign: "center" },
                                    }}
                                  />
                                </Tooltip>
                              ) : items.length > 0 && (
                                <Tooltip title={t("gut_in_range_tooltip")} placement="top">
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                                  </Box>
                                </Tooltip>
                              )}
                              <Box sx={{ display: "flex", alignItems: "center", ml: 1.25 }}>
                                {isOpen
                                  ? <Minus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />
                                  : <Plus size={18} color={theme.palette.mode === "light" ? theme.palette.greydark.C900 : theme.palette.grey.C50} />}
                              </Box>
                            </Box>
                            {hasRecs && (
                              <Box sx={{ display: { xs: "block", sm: "none" }, mt: 0.5 }}>
                                <Tooltip title={t("gut_view_recommendations")} placement="top">
                                  <Stack
                                    direction="row" alignItems="center" spacing={0.75}
                                    onClick={e => { e.stopPropagation(); setRecsDialogSubsystem(subsystemId); setOpenRecs(true); }}
                                    sx={{
                                      display: "inline-flex",
                                      px: 1.5, height: 28, borderRadius: "999px",
                                      bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
                                      cursor: "pointer", transition: "transform 0.15s",
                                      "&:hover": { transform: "scale(1.05)" },
                                    }}
                                  >
                                    <Utensils size={14} color={theme.palette.text.secondary} />
                                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                      {t("gut_food_recommendations_label") || "Nutrition Recommendations"}
                                    </Typography>
                                  </Stack>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: { xs: 1, sm: 2 }, display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {items.map((m) => {
                            const evalBarColor =
                              m.evaluation === "N" ? theme.palette.traffic.green :
                              m.evaluation === "A" ? theme.palette.traffic.orange :
                              theme.palette.grey.C400;

                            return (
                              <Box key={m.id} sx={{
                                borderRadius: 3, overflow: "hidden",
                                bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                                backdropFilter: "blur(20px) saturate(180%)",
                                WebkitBackdropFilter: "blur(20px) saturate(180%)",
                                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
                                boxShadow: theme.palette.mode === "dark"
                                  ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.45)"
                                  : "inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 12px rgba(0,0,0,0.05)",
                              }}>
                                <Box sx={{ display: "flex", alignItems: "center", px: { xs: 1.5, sm: 2.5 }, py: 1, columnGap: 2, rowGap: { xs: 0.5, sm: 2 }, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                                  {/* Status dot + name/description */}
                                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flexGrow: 1, minWidth: 120 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: evalBarColor, mt: "7px" }} />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xl: "1rem" } }}>
                                        {t(m.marker.name_key)}
                                      </Typography>
                                      <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xl: "0.9rem" } }}>
                                        {t(m.marker.description_key)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {/* Mobile-only line break: forces the value onto its own row */}
                                  <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />

                                  {/* Value */}
                                  <Typography variant="h5" sx={{
                                    width: { xs: "auto", sm: 80 }, flexShrink: 0,
                                    textAlign: { xs: "left", sm: "right" },
                                    ml: { xs: "26px", sm: 0 },
                                    fontVariantNumeric: "tabular-nums",
                                  }}>
                                    {m.value != null ? m.value : "—"}{m.unit ? ` ${m.unit}` : ""}
                                  </Typography>

                                  {/* Mobile-only line break: forces the gauge onto its own row */}
                                  <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />

                                  {/* Gauge — fixed width so every row's gauge lines up in a column */}
                                  {m.selected_reference_range && (
                                    <Box sx={{ width: 220, flexShrink: 0, ml: 2, display: "flex", alignItems: "center" }}>
                                      <SegmentedGaugeBar
                                        value={m.value}
                                        referenceRange={m.selected_reference_range}
                                        markerInfo={m.marker.marker_info}
                                        evaluation={m.evaluation}
                                        theme={theme}
                                        segments={20}
                                        height={32}
                                      />
                                    </Box>
                                  )}

                                  {/* Mobile-only line break: forces the status/info/chart icons onto their own row */}
                                  <Box sx={{ flexBasis: "100%", height: 0, display: { xs: "block", sm: "none" } }} />

                                  {/* Status */}
                                  <Box sx={{
                                    minWidth: { xs: "auto", sm: 68 }, display: "flex",
                                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                                    ml: { xs: 2, sm: 0 }, flexShrink: 0,
                                  }}>
                                    {m.evaluation === "N" ? (
                                      <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                                    ) : m.evaluation === "A" ? (
                                      <Chip
                                        label="ABNORMAL"
                                        size="small"
                                        sx={{
                                          bgcolor: theme.palette.traffic.orange, color: "#fff",
                                          fontWeight: 700, fontSize: "0.65rem", height: 22,
                                          borderRadius: "6px", "& .MuiChip-label": { px: 1 },
                                        }}
                                      />
                                    ) : null}
                                  </Box>

                                  {/* Info */}
                                  <Tooltip title="Info" placement="top">
                                    <IconButton size="small" onClick={e => { e.stopPropagation(); setSelectedMarker(m); setOpenInfo(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                      <Info size={18} color={theme.palette.secondary.main} />
                                    </IconButton>
                                  </Tooltip>

                                  {/* Chart */}
                                  <Tooltip title={t("gut_marker_chart")} placement="top">
                                    <IconButton size="small" onClick={e => handleOpenChart(e, m)} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                                      <ChartNoAxesCombined size={18} color={theme.palette.primary.main} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            );
                          })}
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </Grid>
                );
              })
          )}

          {/* ── What this means for your gut ─────────────────────────────────── */}
          {!measurementsLoading && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", mb: 1, whiteSpace: "nowrap" }}>
                {t("gut_insights_grid_title") || "What this means for your gut"}
              </Typography>
              <Box sx={{
                borderRadius: 4,
                overflow: "hidden",
                bgcolor: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
                backdropFilter: "blur(28px) saturate(180%)",
                WebkitBackdropFilter: "blur(28px) saturate(180%)",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
                boxShadow: theme.palette.mode === "dark"
                  ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
                  : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
              }}>
                <Stack divider={<Divider sx={{ mx: 2.5, opacity: 0.5 }} />}>
                  {[2, 3, 4, 5].map(subsystemId => (
                    <SubsystemInsightCard
                      key={subsystemId}
                      icon={SUBSYSTEM_ICONS[subsystemId]}
                      title={subsystemLabels[subsystemId]}
                      text={agentMicrobiome?.[SUBSYSTEM_TO_INSIGHT_FIELD[subsystemId]]}
                      emptyText={t("gut_key_insights_empty") || "No analysis available"}
                      theme={theme}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}

          {/* ── Inflammatory Pathways + Bacteria ─────────────────────────────── */}
          {!measurementsLoading && (
            <>
              {/* Inflammatory Pathways inline */}
              {lps && (
                <>
                  <Typography variant="body1" fontWeight={700} sx={{ color: theme.palette.mode === "dark" ? "grey.C10" : "greydark.C900", mt: 4, mb: 1, whiteSpace: "nowrap" }}>
                    {t("gut_inflammatory_header")}
                  </Typography>
                  <InflammatoryPathwaysCard lps={lps} accentColor={theme.palette.tertiary.C300} />
                </>
              )}

              <GutBacteriaComposition
                records={bacteriaRecords}
                loading={bacteriaLoading}
                taxonomySegments={6}
                levelLabel="Genus Level"
              />
            </>
          )}
        </Box>
      </Grid>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}

      {/* Marker Info */}
      <CustomDialogSmall
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t("gut_marker_info")}
        content={
          <TextInfoComp
            description={selectedMarker?.marker?.marker_info_key ? t(selectedMarker.marker.marker_info_key, selectedMarker.marker.marker_info) : ""}
            lowtext={selectedMarker?.marker?.marker_info_lowtext_key ? t(selectedMarker.marker.marker_info_lowtext_key) : ""}
            lowrec={selectedMarker?.marker?.marker_info_lowrec_key ? t(selectedMarker.marker.marker_info_lowrec_key) : ""}
            rangetext={selectedMarker?.marker?.marker_info_rangetext_key ? t(selectedMarker.marker.marker_info_rangetext_key) : ""}
            hightext={selectedMarker?.marker?.marker_info_hightext_key ? t(selectedMarker.marker.marker_info_hightext_key) : ""}
            highrec={selectedMarker?.marker?.marker_info_highrec_key ? t(selectedMarker.marker.marker_info_highrec_key) : ""}
          />
        }
      />

      {/* Chart + history */}
      <CustomDialogSmall
        open={openChart}
        onClose={() => { setOpenChart(false); setChartData(null); }}
        title={selectedMarker ? t(selectedMarker.marker.name_key) : t("gut_marker_chart")}
        content={
          chartLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : chartData ? (
            <GutMarkerChart chartData={chartData} markerInfo={selectedMarker?.marker?.marker_info} theme={theme} t={t} />
          ) : (
            <Typography sx={{ p: 3 }}>{t("gut_no_chart_data")}</Typography>
          )
        }
      />

      {/* Recommendations */}
      <CustomDialogSmall
        open={openRecs}
        onClose={() => { setOpenRecs(false); setRecsDialogSubsystem(null); }}
        title={recsDialogSubsystem ? (subsystemLabels[recsDialogSubsystem] || `Subsystem ${recsDialogSubsystem}`) : "Recommendations"}
        content={<GutRecommendations items={activeRecsItems.items} recipe={activeRecsItems.recipe} />}
      />


      {/* Gut Microbiome Profile – Enterotype */}
      <CustomDialogSmall
        open={enterotypeOpen}
        onClose={() => setEnterotypeOpen(false)}
        title={t("entero1")}
        content={
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            {type && <EnterotypeCard value={parseFloat(type.value)} />}
          </Box>
        }
      />

      {/* Bacteria by Category */}
      <CustomDialog
        open={bacteriaCategoryOpen}
        onClose={() => setBacteriaCategoryOpen(false)}
        title={t("gut_bacteria_analysis_dialog")}
        content={<Box sx={{ pt: 1 }}><GutBacteriaCategory date={selectedDate} /></Box>}
      />

      {/* All Bacteria */}
      <CustomDialog
        open={bacteriaAllOpen}
        onClose={() => setBacteriaAllOpen(false)}
        title={t("gut_bacteria_summary_dialog")}
        content={
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
              {t("gut_bacteria_summary_subheader")}
            </Typography>
            <GutBacteriaAll date={selectedDate} />
          </Box>
        }
      />

    </Grid>
  );
}
